import { useEffect, useState } from "react";
import {
    Box, Typography, Grid, Card, CardContent, Chip, Divider,
    Avatar, Stack, Button, Dialog, DialogTitle, DialogContent,
    TextField, DialogActions, Paper, IconButton, Tooltip, Badge,
    LinearProgress, Tab, Tabs, Alert, Skeleton, useTheme
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { getImageFullUrl, pageRoute } from "../utils/common";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import StorefrontIcon from "@mui/icons-material/Storefront";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import BusinessIcon from "@mui/icons-material/Business";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AdminVendorPayoutView from "../components/AdminVendorPayoutView";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
    d
        ? new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        })
        : "—";

const statusMuiColor = (s) =>
    ({ active: "success", inactive: "default", suspended: "warning" }[s] ?? "default");

const approvalMuiColor = (s) =>
    ({ approved: "success", rejected: "error", pending: "warning" }[s] ?? "default");

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionCard = ({ title, icon, children, action }) => (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                        {title}
                    </Typography>
                </Stack>
                {action}
            </Stack>
            {children}
        </CardContent>
    </Card>
);

const InfoRow = ({ label, value, mono }) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        py={1}
        sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "&:last-child": { borderBottom: "none" },
        }}
    >
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 170, pt: 0.2 }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            color="text.primary"
            fontFamily={mono ? "'Fira Code', monospace" : "inherit"}
            sx={{ textAlign: "right", wordBreak: "break-all" }}
        >
            {value ?? "—"}
        </Typography>
    </Stack>
);

const StatCard = ({ label, value, color }) => (
    <Paper
        variant="outlined"
        sx={{
            borderRadius: 2.5,
            p: 2,
            textAlign: "center",
            borderColor: color !== "inherit" ? `${color}.light` : "divider",
        }}
    >
        <Typography
            variant="h4"
            fontWeight={800}
            color={color !== "inherit" ? `${color}.main` : "text.secondary"}
        >
            {value ?? 0}
        </Typography>
        <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={1}>
            {label}
        </Typography>
    </Paper>
);

const DocPreview = ({ label, file }) => {
    const [open, setOpen] = useState(false);
    const url = getImageFullUrl(file);
    return (
        <>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {label}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Preview">
                            <IconButton size="small" color="primary" onClick={() => setOpen(true)}>
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                    </Stack>
                </Stack>
                <Box
                    mt={1}
                    sx={{ height: 80, borderRadius: 1.5, overflow: "hidden", cursor: "pointer" }}
                    onClick={() => setOpen(true)}
                >
                    <Box
                        component="img"
                        src={url}
                        alt={label}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                </Box>
            </Paper>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{label}</DialogTitle>
                <DialogContent dividers>
                    <Box component="img" src={url} alt={label} sx={{ width: "100%", borderRadius: 2 }} />
                </DialogContent>
                <DialogActions>

                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const VerifyChip = ({ label, verified }) => (
    <Chip
        size="small"
        color={verified ? "success" : "error"}
        variant="outlined"
        icon={verified ? <VerifiedIcon sx={{ fontSize: "14px !important" }} /> : undefined}
        label={verified ? `${label} Verified` : `${label} Unverified`}
        sx={{ fontWeight: 600, fontSize: 11 }}
    />
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function VendorView() {
    const { vendorId } = useParams();
    const theme = useTheme();
    const navigate = useNavigate()

    const [vendorData, setVendorData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [tab, setTab] = useState(0);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchVendor = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get(`/api/admin/vendor/${vendorId}`);
            setVendorData(res?.data?.data);
        } catch {
            toast.error("Failed to load vendor");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (vendorId) fetchVendor();
    }, [vendorId]);


    const handleApprove = async () => {
        try {
            setActionLoading(true);
            await apiClient.put(`/api/admin/vendor/${vendorId}`, { approval_status: 'approved' });
            toast.success("Vendor approved successfully");
            fetchVendor();
        } catch {
            toast.error("Failed to approve vendor");
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!reason.trim()) {
            toast.warning("Please provide a rejection reason");
            return;
        }
        try {
            setActionLoading(true);
            await apiClient.put(`/api/admin/vendor/${vendorId}`, { reason: reason, approval_status: 'rejected' });
            toast.success("Vendor rejected");
            setRejectOpen(false);
            setReason("");
            fetchVendor();
        } catch {
            toast.error("Failed to reject vendor");
        } finally {
            setActionLoading(false);
        }
    };

    const handleVerifyEmail = async () => {
        try {
            setActionLoading(true);
            await apiClient.post(`/api/admin/vendor/verify-email/${vendorId}`);
            toast.success("Email marked as verified");
            fetchVendor();
        } catch {
            toast.error("Failed to verify email");
        } finally {
            setActionLoading(false);
        }
    };

    const handleVerifyMobile = async () => {
        try {
            setActionLoading(true);
            await apiClient.post(`/api/admin/vendor/verify-mobile/${vendorId}`);
            toast.success("Mobile marked as verified");
            fetchVendor();
        } catch {
            toast.error("Failed to verify mobile");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Loading ─────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ p: 4 }}>
                <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3, mb: 3 }} />
                <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                        <Grid item size={{ xs: 12, md: 4 }} key={i}>
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    const vendor = vendorData?.vendor;
    const business = vendor?.businessDetails;
    const stats = vendorData?.productStats ?? {};
    const approvalStatus = business?.approval_status ?? "pending";

    const handleIsVerifyChange = async (id, newStatus) => {
        try {
            await apiClient.put(`/api/admin/vendor/${id}`, { isVerify: newStatus });
            toast.success("Verify status updated");
            fetchVendor();
        } catch {
            toast.error("Failed to update verify status");
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await apiClient.put(`/api/admin/vendor/${id}`, { status: newStatus });
            toast.success("Status updated");
            fetchVendor();
        } catch {
            toast.error("Failed to update status");
        }
    };


    const pageChange = (url) => {
        navigate(url)
    }

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: { xs: 2, md: 4 } }}>

            {/* ── HEADER CARD ── */}
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ sm: "center" }}>

                        {/* Avatar */}
                        <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            badgeContent={
                                <Box
                                    sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: `2px solid ${theme.palette.background.paper}`,
                                        bgcolor:
                                            vendor?.status === "active"
                                                ? theme.palette.success.main
                                                : theme.palette.error.main,
                                    }}
                                />
                            }
                        >
                            <Avatar
                                src={getImageFullUrl(vendor?.avatar)}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    border: "3px solid",
                                    borderColor: "primary.main",
                                    fontSize: 28,
                                    bgcolor: "primary.lighter",
                                }}
                            >
                                {vendor?.name?.[0]?.toUpperCase()}
                            </Avatar>
                        </Badge>

                        {/* Info */}
                        <Box flex={1}>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" mb={0.5}>
                                <Typography variant="h5" fontWeight={800} color="text.primary">
                                    {vendor?.name}
                                </Typography>
                                <Chip
                                    size="small"
                                    color={statusMuiColor(vendor?.status)}
                                    label={vendor?.status}
                                    sx={{ fontWeight: 700, fontSize: 11, textTransform: "capitalize" }}
                                />
                                <Chip
                                    size="small"
                                    color={approvalMuiColor(approvalStatus)}
                                    label={approvalStatus}
                                    sx={{ fontWeight: 700, fontSize: 11, textTransform: "capitalize" }}
                                />
                                {vendor?.isVerify && (
                                    <Chip
                                        size="small"
                                        color="success"
                                        icon={<VerifiedIcon sx={{ fontSize: "14px !important" }} />}
                                        label="KYC Verified"
                                        sx={{ fontWeight: 700, fontSize: 11 }}
                                    />
                                )}
                            </Stack>
                            <Stack direction="row" spacing={3} flexWrap="wrap">
                                <Stack direction="row" spacing={0.7} alignItems="center">
                                    <EmailIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                    <Typography variant="body2" color="text.secondary">{vendor?.email}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.7} alignItems="center">
                                    <PhoneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                    <Typography variant="body2" color="text.secondary">{vendor?.mobile}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.7} alignItems="center">
                                    <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Joined {fmtDate(vendor?.createdAt)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* ── PRODUCT STATS ── */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <h4 style={{ margin: 0 }}>
                    Products Details
                </h4>
                <Button
                    variant="outlined"
                    size="small"
                    color={'primary'}
                    startIcon={<ListAltIcon />}
                    onClick={() => pageChange(pageRoute.productList + '?id=' + vendorId)}

                    sx={{ fontWeight: 700, fontSize: 12 }}
                >
                    View All Products
                </Button>
            </div>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2} mb={3}>
                {[
                    { label: "Total", value: stats.total, color: "primary" },
                    { label: "Active", value: stats.active, color: "success" },
                    { label: "Inactive", value: stats.inactive, color: "inherit" },
                    { label: "Approved", value: stats.approved, color: "info" },
                    { label: "Pending", value: stats.pending, color: "warning" },
                    { label: "Rejected", value: stats.rejected, color: "error" },
                ].map((s) => (
                    <Grid item size={{ xs: 6, md: 2, sm: 4 }} key={s.label}>
                        <StatCard {...s} />
                    </Grid>
                ))}
            </Grid>

            {/* ── TABS ── */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Account" />
                    <Tab label="Business & KYC" />
                    <Tab label="Documents" />
                    <Tab label="Address" />
                    <Tab label="Payout Details" />
                </Tabs>
            </Box>

            {/* ═══ TAB 0 — ACCOUNT ═══ */}
            {tab === 0 && (
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <SectionCard title="Account Info" icon={<AdminPanelSettingsIcon />} action={
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color={vendor?.status === "active" ? "error" : "success"}
                                    startIcon={vendor?.status === "active" ? <CancelOutlinedIcon /> : <CheckCircleOutlineIcon />}

                                    onClick={() => {
                                        handleStatusChange(
                                            vendor.id,
                                            vendor?.status === "active" ? "inactive" : "active"
                                        )
                                    }}
                                    disabled={actionLoading}
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                    {vendor?.status === "active" ? "Deactivate" : "Activate"}
                                </Button>
                            </Stack>
                        }>
                            <InfoRow label="Vendor ID" value={vendor?.id} mono />
                            <InfoRow label="Name" value={vendor?.name} />
                            <InfoRow label="Email" value={vendor?.email} />
                            <InfoRow label="Mobile" value={vendor?.mobile} />
                            <InfoRow
                                label="Status"
                                value={
                                    <Chip
                                        size="small"
                                        color={statusMuiColor(vendor?.status)}
                                        label={vendor?.status}
                                        sx={{ fontWeight: 700, textTransform: "capitalize" }}
                                    />
                                }
                            />
                            {/* <InfoRow label="Last Login" value={fmtDate(vendor?.last_login_date)} /> */}
                            <InfoRow label="Created" value={fmtDate(vendor?.createdAt)} />
                            <InfoRow label="Updated" value={fmtDate(vendor?.updatedAt)} />
                        </SectionCard>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 6 }}>
                        <SectionCard
                            title="Verification Status"
                            icon={<VerifiedIcon />}
                            action={
                                <Stack direction="row" spacing={1}>
                                    {/* {!vendor?.verify_email && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<MarkEmailReadIcon />}
                                            disabled={actionLoading}
                                            onClick={handleVerifyEmail}
                                            sx={{ fontSize: 11, fontWeight: 700 }}
                                        >
                                            Verify Email
                                        </Button>
                                    )} */}
                                    {/* {!vendor?.verify_mobile && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                            startIcon={<SmartphoneIcon />}
                                            disabled={actionLoading}
                                            onClick={handleVerifyMobile}
                                            sx={{ fontSize: 11, fontWeight: 700 }}
                                        >
                                            Verify Mobile
                                        </Button>
                                    )} */}
                                </Stack>
                            }
                        >
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2}>
                                <VerifyChip label="Email" verified={vendor?.verify_email} />
                                {/* <VerifyChip label="Mobile" verified={vendor?.verify_mobile} /> */}
                                <Chip
                                    size="small"
                                    color={vendor?.isVerify ? "success" : "warning"}
                                    variant="outlined"
                                    label={vendor?.isVerify ? "KYC Complete" : "KYC Pending"}
                                    sx={{ fontWeight: 700, fontSize: 11 }}
                                />
                            </Stack>
                            {/* 
                            <Divider sx={{ my: 2 }} />

                            <InfoRow label="Email Verify Attempts" value={vendor?.email_verify_attempts} />
                            <InfoRow label="Email Last Sent" value={fmtDate(vendor?.email_verify_last_sent)} />
                            <InfoRow label="Mobile OTP Attempts" value={vendor?.mobile_otp_attempts} />
                            <InfoRow label="Mobile OTP Last Sent" value={fmtDate(vendor?.mobile_otp_last_sent)} />
                            <InfoRow label="Reset Request Count" value={vendor?.resetRequestCount} /> */}
                        </SectionCard>

                        {approvalStatus === "rejected" && business?.rejection_reason && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                <strong>Rejection Reason:</strong> {business.rejection_reason}
                            </Alert>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* ═══ TAB 1 — BUSINESS & KYC ═══ */}
            {tab === 1 && (
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, md: 6 }} >
                        <SectionCard title="Business Details" icon={<BusinessIcon />}
                            action={
                                <Stack direction="row" spacing={1}>
                                    {approvalStatus === "pending" && (
                                        <>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="success"
                                                startIcon={<CheckCircleOutlineIcon />}
                                                disabled={actionLoading}
                                                onClick={handleApprove}
                                                sx={{ fontWeight: 700, fontSize: 12 }}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                startIcon={<CancelOutlinedIcon />}
                                                disabled={actionLoading}
                                                onClick={() => setRejectOpen(true)}
                                                sx={{ fontWeight: 700, fontSize: 12 }}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                    {approvalStatus === "rejected" && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="success"
                                            startIcon={<CheckCircleOutlineIcon />}
                                            disabled={actionLoading}
                                            onClick={handleApprove}
                                            sx={{ fontWeight: 700, fontSize: 12 }}
                                        >
                                            Re-Approve
                                        </Button>
                                    )}
                                    {approvalStatus === "approved" && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            startIcon={<CancelOutlinedIcon />}
                                            disabled={actionLoading}
                                            onClick={() => setRejectOpen(true)}
                                            sx={{ fontWeight: 700, fontSize: 12 }}
                                        >
                                            Reject
                                        </Button>
                                    )}
                                </Stack>
                            }
                        >
                            <InfoRow label="Business Name" value={business?.businessName} />
                            <InfoRow
                                label="Seller Type"
                                value={
                                    <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label={business?.seller_type}
                                        sx={{ fontWeight: 700, textTransform: "capitalize" }}
                                    />
                                }
                            />
                            <InfoRow
                                label="Approval Status"
                                value={
                                    <Chip
                                        size="small"
                                        color={approvalMuiColor(approvalStatus)}
                                        label={approvalStatus}
                                        sx={{ fontWeight: 700, textTransform: "capitalize" }}
                                    />
                                }
                            />
                            <InfoRow label="Submitted" value={business?.is_submitted ? "Yes" : "No"} />
                            <InfoRow label="Submitted At" value={fmtDate(business?.submitted_at)} />
                            <InfoRow label="Approved At" value={fmtDate(business?.approved_at)} />
                            <InfoRow label="Rejected At" value={fmtDate(business?.rejected_at)} />
                            <InfoRow label="Rejection Reason" value={business?.rejection_reason} />
                        </SectionCard>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 6 }} >
                        <SectionCard title="Tax & Identity" icon={<AccountBalanceIcon />} action={
                            <Stack direction="row" spacing={1}>
                                {!vendor?.isVerify && (
                                    <>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="success"
                                            startIcon={<CheckCircleOutlineIcon />}
                                            disabled={actionLoading}
                                            onClick={() => { handleIsVerifyChange(vendor?.id, true) }}
                                            sx={{ fontWeight: 700, fontSize: 12 }}
                                        >
                                            Verify
                                        </Button>
                                    </>
                                )}
                                {vendor?.isVerify && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="error"
                                        startIcon={<CancelOutlinedIcon />}
                                        disabled={actionLoading}
                                        onClick={() => { handleIsVerifyChange(vendor?.id, false) }}
                                        sx={{ fontWeight: 700, fontSize: 12 }}
                                    >
                                        Unverified
                                    </Button>
                                )}
                            </Stack>
                        }>
                            <InfoRow label="PAN Number" value={business?.pan_number} mono />
                            <InfoRow label="GST Number" value={business?.gst_number} mono />
                            <InfoRow label="Business ID" value={business?.id} mono />
                        </SectionCard>

                        {/* KYC Progress */}
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={2}>
                                    KYC Completion
                                </Typography>
                                {[
                                    { label: "Email Verified", done: vendor?.verify_email },
                                    // { label: "Mobile Verified", done: vendor?.verify_mobile },
                                    { label: "Business Submitted", done: business?.is_submitted },
                                    { label: "Business Approved", done: approvalStatus === "approved" },
                                    { label: "KYC Complete", done: vendor?.isVerify },
                                ].map((step) => (
                                    <Stack
                                        key={step.label}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {step.label}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            color={step.done ? "success" : "warning"}
                                            variant="outlined"
                                            label={step.done ? "Done" : "Pending"}
                                            sx={{ fontWeight: 700, fontSize: 10 }}
                                        />
                                    </Stack>
                                ))}
                                <Box mt={2}>
                                    <LinearProgress
                                        variant="determinate"
                                        color="primary"
                                        value={
                                            ([
                                                vendor?.verify_email,
                                                // vendor?.verify_mobile,
                                                business?.is_submitted,
                                                approvalStatus === "approved",
                                                vendor?.isVerify,
                                            ].filter(Boolean).length /
                                                3) *
                                            100
                                        }
                                        sx={{ borderRadius: 4, height: 6 }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* ═══ TAB 2 — DOCUMENTS ═══ */}
            {tab === 2 && (
                <Grid container spacing={2.5}>
                    {[
                        { label: "PAN Card", file: business?.pan_card_file },
                        { label: "Aadhaar Card", file: business?.aadhaar_card_file },
                        { label: "Signature", file: business?.signature_file },
                        { label: "GST Certificate", file: business?.gst_certificate_file },
                    ].map((doc) => (
                        <Grid item size={{ xs: 12, md: 3, sm: 6 }} key={doc.label}>
                            {doc.file ? (
                                <DocPreview label={doc.label} file={doc.file} />
                            ) : (
                                <Paper
                                    variant="outlined"
                                    sx={{ borderRadius: 2, p: 2.5, textAlign: "center" }}
                                >
                                    <FingerprintIcon sx={{ color: "text.disabled", fontSize: 32, mb: 1 }} />
                                    <Typography variant="caption" color="text.disabled">
                                        {doc.label} — Not uploaded
                                    </Typography>
                                </Paper>
                            )}
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* ═══ TAB 3 — ADDRESS ═══ */}
            {tab === 3 && (
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                        <SectionCard title="Office Address" icon={<LocationOnIcon />}>
                            <InfoRow label="Address Line 1" value={business?.office_address_line1} />
                            <InfoRow label="Address Line 2" value={business?.office_address_line2} />
                            <InfoRow label="City" value={business?.office_city} />
                            <InfoRow label="State" value={business?.office_state} />
                            <InfoRow label="Pincode" value={business?.office_pincode} />
                            <InfoRow label="Country" value={business?.office_country} />
                        </SectionCard>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }} >
                        <SectionCard
                            title="Pickup Address"
                            icon={<StorefrontIcon />}
                            action={
                                business?.same_as_office_address ? (
                                    <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label="Same as Office"
                                        sx={{ fontWeight: 700, fontSize: 11 }}
                                    />
                                ) : null
                            }
                        >
                            {business?.same_as_office_address ? (
                                <Box sx={{ textAlign: "center", py: 3 }}>
                                    <LocationOnIcon sx={{ color: "text.disabled", fontSize: 36 }} />
                                    <Typography variant="body2" color="text.secondary" mt={1}>
                                        Same as office address
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <InfoRow label="Address Line 1" value={business?.pickup_address_line1} />
                                    <InfoRow label="Address Line 2" value={business?.pickup_address_line2} />
                                    <InfoRow label="City" value={business?.pickup_city} />
                                    <InfoRow label="State" value={business?.pickup_state} />
                                    <InfoRow label="Pincode" value={business?.pickup_pincode} />
                                    <InfoRow label="Country" value={business?.pickup_country} />
                                </>
                            )}
                        </SectionCard>
                    </Grid>
                </Grid>
            )}
            {
                tab === 4 && (
                    <AdminVendorPayoutView data={vendor?.payoutDetails || {}} id={vendor?.id} fetchVendor={fetchVendor}/>
                )
            }
            {/* ── REJECT DIALOG ── */}
            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Reject Vendor</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Please provide a clear reason for rejection. This will be visible to the vendor.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Documents are blurry, PAN number mismatch..."
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setRejectOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleReject}
                        disabled={actionLoading || !reason.trim()}
                        sx={{ fontWeight: 700 }}
                    >
                        Confirm Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}